import {queryOptions, type QueryClient} from "@tanstack/react-query";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import type {Result} from "@vesta/error-handling-remix/result";
import {
    workspaceCapabilitiesValidator,
    type WorkspaceCapability,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapability";
import type {WorkspaceCapabilityType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";
import {WORKSPACE__CAPABILITIES__QUERY_KEY} from "~/utilities/queryKeys";

/** Admin lifecycle views need disabled rows too; the runtime cacheable endpoint only returns enabled capabilities. */
export async function getAdminWorkspaceCapabilitiesWithCaching({
    accessToken,
    intellsysBaseUrl,
    queryClient,
    workspaceId,
}: {
    accessToken: string;
    intellsysBaseUrl: string;
    queryClient: QueryClient;
    workspaceId: Uuid;
}): Promise<Result<Array<WorkspaceCapability<WorkspaceCapabilityType>>>> {
    return queryClient.ensureQueryData(adminWorkspaceCapabilitiesQueryOptions({
        accessToken,
        intellsysBaseUrl,
        workspaceId,
    }));
}

export function adminWorkspaceCapabilitiesQueryOptions({
    accessToken,
    intellsysBaseUrl,
    workspaceId,
}: {
    accessToken: string;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
}) {
    return queryOptions({
        queryKey: [WORKSPACE__CAPABILITIES__QUERY_KEY, workspaceId, "admin"],
        queryFn: () =>
            robustJsonResultClientFetch(
                `${intellsysBaseUrl}/sub-system/pluto/admin/workspace-capabilities/get`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: JSON.stringify({workspaceId: workspaceId}),
                },
                workspaceCapabilitiesValidator,
            ),
        retry: false,
        staleTime: Infinity,
    });
}
